import React from 'react';
import { motion } from 'framer-motion';
import { Quote, Radio, Share2, ExternalLink, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { HOST_IMAGE, EXPERT_IMAGES } from '@/data/experts';

export const QuotesList = ({ quotes, podcastTopic }) => {
  if (!quotes.length) return null;

  return (
    <div className="mt-6" data-testid="quotes-section">
      <div className="flex items-center gap-2 mb-4">
        <Quote className="w-5 h-5 text-purple-400" />
        <h2 className="text-base font-medium" style={{ color: '#F8FAFC' }}>Shareable Quotes</h2>
      </div>
      <div className="space-y-4">
        {quotes.map((q, i) => (
          <QuoteCard key={q.expert_id + i} quote={q} index={i} podcastTopic={podcastTopic} />
        ))}
      </div>
    </div>
  );
};

const QuoteCard = ({ quote, index, podcastTopic }) => {
  const avatarSrc = EXPERT_IMAGES[quote.expert_id] || HOST_IMAGE;
  const shareText = `"${quote.text}" — ${quote.speaker} on "${podcastTopic}" | Opposite Direction Podcast`;
  const shareUrl = window.location.href;
  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
  const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
  const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 * index }}
      className="rounded-2xl border overflow-hidden relative group"
      style={{ backgroundColor: 'rgba(15,15,18,0.9)', borderColor: 'rgba(255,255,255,0.06)' }}
      data-testid={`quote-card-${index}`}
    >
      <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: 'linear-gradient(90deg, #6D28D9, #A78BFA, #6D28D9)' }} />
      <div className="p-5 sm:p-6">
        <div className="absolute top-4 right-4 opacity-[0.05]"><Quote className="w-16 h-16" /></div>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full border-2 overflow-hidden" style={{ borderColor: '#6D28D9' }}>
            <img src={avatarSrc} alt={quote.speaker} className="w-full h-full object-cover" />
          </div>
          <div>
            <p className="text-sm font-medium" style={{ color: '#F8FAFC' }}>{quote.speaker}</p>
            <p className="text-[10px]" style={{ color: '#64748B' }}>{quote.title}</p>
          </div>
        </div>
        <p className="text-base sm:text-lg leading-relaxed italic mb-4" style={{ color: '#E2E8F0', fontFamily: "'Cormorant Garamond', serif" }}>
          "{quote.text}"
        </p>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <Radio className="w-3 h-3 text-purple-500" />
            <span className="text-[9px] tracking-wider uppercase" style={{ color: '#6D28D9' }}>Opposite Direction</span>
          </div>
          <div className="flex items-center gap-1">
            <a href={twitterUrl} target="_blank" rel="noopener noreferrer"
              className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-500 hover:text-white hover:bg-white/5 transition-colors" title="Share on X" data-testid={`quote-share-x-${index}`}>
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
            <a href={linkedInUrl} target="_blank" rel="noopener noreferrer"
              className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-500 hover:text-white hover:bg-white/5 transition-colors" title="Share on LinkedIn" data-testid={`quote-share-linkedin-${index}`}>
              <Share2 className="w-3.5 h-3.5" />
            </a>
            <a href={fbUrl} target="_blank" rel="noopener noreferrer"
              className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-500 hover:text-white hover:bg-white/5 transition-colors" title="Share on Facebook">
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
            <button onClick={() => { navigator.clipboard.writeText(shareText); toast.success('Quote copied!'); }}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-500 hover:text-white hover:bg-white/5 transition-colors" title="Copy quote" data-testid={`quote-copy-${index}`}>
              <Copy className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};
